import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import styled, { useTheme } from "styled-components";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { DownloadLink } from "simplydo/react/components/DownloadLink";
import { IconHeader } from "simplydo/react/components/IconHeader";
import { VideoPlayer } from "simplydo/react/components/VideoPlayer";
import { RichTextViewer } from "simplydo/react/richtext";
import {
  Button,
  Divider,
  Dropdown,
  Icon,
  Message,
  Popup,
  Segment,
} from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import RedactedInfo from "components/lib/RedactedInfo";
import { FakeLink } from "components/lib/UI";

import type { ChallengeDetail, GenAIChallengeDraft } from "../types";
import ChallengeMyIdeas from "./Ideas/MyIdeas";
import ChallengeProblems from "./Problems";

const NewIdeaTrigger = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ChallengeClosedActions = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  .ui.button {
    ${({ theme }) =>
      theme.sizes.isMobile ?
        `
      &:not(:first-of-type) {
        margin-top: 10px;
      }
    `
      : `
      &:not(:first-of-type) {
        margin-left: 10px;
      }
    `}
  }
`;

const OverviewActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 25px;
  width: 100%;
`;

const redactedInfoWidths = [72, 81, 65, 78, 69, 84];

const OverviewActions = styled.div`
  display: flex;
  min-width: 66%;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  align-items: ${({ theme }) =>
    theme.sizes.isMobile ? "center" : "flex-start"};
  justify-content: ${({ theme }) =>
    theme.sizes.isMobile ? "flex-start" : "space-evenly"};
  .ui.dropdown {
    height: 40px;
  }
  .ui.button,
  .ui.dropdown,
  ${NewIdeaTrigger} {
    flex-grow: 1;
    display: block;
    ${({ theme }) => theme.sizes.isMobile && "width: 100%;"}
    &:not(:last-of-type) {
      ${({ theme }) =>
        theme.sizes.isMobile ? "margin-bottom: 10px" : "margin-right: 5px"};
    }
  }
  ${NewIdeaTrigger} {
    display: flex;
  }
`;

type OverviewPreviewProps = { challenge: GenAIChallengeDraft; isPreview: true };

type OverviewLiveProps = {
  challenge: ChallengeDetail;
  isPreview?: false;
  newIdea: () => void;
  ipStatement?: OpenAPI.Schemas["IpStatement"] | null;
  ipStatementHandled: boolean;
  ipManagementInfo?: { hasSensitiveInfo: boolean; userHasSigned: boolean };
  setIpStatementAgreementOpen: (open: boolean) => void;
  redirectToLogin: () => void;
};

type OverviewProps = OverviewPreviewProps | OverviewLiveProps;

type ChallengeOverviewDisplay = GenAIChallengeDraft & {
  videoUrl?: string;
  ideaCount?: number;
};

const Overview = (props: OverviewProps) => {
  const isPreview = props.isPreview === true;
  const challengeDetail = isPreview ? undefined : props.challenge;
  const challenge = props.challenge as ChallengeOverviewDisplay;
  const newIdea = isPreview ? () => {} : props.newIdea;
  const ipStatement = isPreview ? null : props.ipStatement;
  const ipStatementHandled = isPreview ? true : props.ipStatementHandled;
  const ipManagementInfo = isPreview ? undefined : props.ipManagementInfo;
  const setIpStatementAgreementOpen =
    isPreview ? () => {} : props.setIpStatementAgreementOpen;
  const redirectToLogin = isPreview ? () => {} : props.redirectToLogin;
  const { t } = useTranslation();
  const [showMyIdeas, setShowMyIdeas] = useState(true);
  const theme = useTheme();
  const { video, videoUrl, reward, links, attachments, stage } = challenge;
  const {
    description = "",
    instructions,
    requirements,
    helpfulTips,
  } = challenge;
  const user = useAppSelector((state) => state.user);
  const canMakeIdea =
    challengeDetail ? util.canCreateIdea(user, challengeDetail) : false;

  const myIdeasQuery = useQuery(
    initUseQuery(
      api.challenges.getIdeas,
      [challengeDetail?._id ?? "", { scope: "me" }],
      { isEnabled: !!user?._id && !!challengeDetail?._id },
    ),
  );
  const myUnsubmittedIdeas = useMemo(
    () => (myIdeasQuery.data?.ideas ?? []).filter((idea) => !idea.isSubmitted),
    [myIdeasQuery.data?.ideas],
  );
  return (
    <div>
      {!isPreview && challengeDetail ?
        <ChallengeProblems challenge={challengeDetail} />
      : null}
      {challenge.stage === "closed" ?
        <Message info>
          <Message.Header>
            This {t("generic.challenge")} is currently closed to new{" "}
            {t("generic.ideas")}
          </Message.Header>
          <ChallengeClosedActions>
            {(
              challenge.ideaVisibility === "users" ||
              (challengeDetail &&
                util.canManageChallenge(user, challengeDetail)) ||
              (challengeDetail &&
                util.hasPermission(
                  user,
                  "challenge.viewIdeas",
                  challengeDetail._id,
                ))
            ) ?
              <Message.Content>
                You can still{" "}
                <Link to={`/challenges/${challenge._id}/ideas`}>
                  {challenge.votingVisibility === "users" ?
                    "view, like, and comment on"
                  : "view and comment on"}
                </Link>{" "}
                {t("generic.idea")} submissions.
                {(
                  challenge.projectManagementVisibility === "users" ||
                  (challengeDetail &&
                    util.canManageChallenge(user, challengeDetail)) ||
                  (challengeDetail &&
                    util.hasPermission(
                      user,
                      "challenge.viewProjectBoard",
                      challengeDetail._id,
                    ))
                ) ?
                  <span>
                    {" "}
                    You may also{" "}
                    <Link
                      to={`/challenges/${challenge._id}/ideas?queryView=board`}
                    >
                      view the project board
                    </Link>{" "}
                    to view and manage the progress of {t("generic.idea")}{" "}
                    implementations.
                  </span>
                : null}
              </Message.Content>
            : null}
          </ChallengeClosedActions>
        </Message>
      : null}
      {challengeDetail ?
        <ChallengeMyIdeas
          challengeId={challengeDetail._id}
          showMyIdeas={showMyIdeas}
          setShowMyIdeas={setShowMyIdeas}
          style={{ marginBottom: 20 }}
        />
      : null}
      {(video || description) && (
        <h3
          className="challenge-tour-step-2"
          style={{ textTransform: "uppercase" }}
        >
          {t("challenge.details.overview")}
        </h3>
      )}
      {video && videoUrl && (
        <div>
          <VideoPlayer src={videoUrl} canExpand />
          <Divider hidden />
        </div>
      )}
      {description && (
        <div>
          <RichTextViewer value={description} />
          <Divider hidden />
        </div>
      )}

      {reward && (
        <div>
          <Segment>
            <IconHeader
              as="h3"
              icon="check circle outline"
              header={t("challenge.details.benefits")}
              content={<RichTextViewer value={reward} />}
              wrapped={theme.sizes.isMobile}
            />
          </Segment>
          <Divider section />
        </div>
      )}

      {instructions && (
        <div>
          <h3 style={{ textTransform: "uppercase" }}>
            {t("challenge.details.instructions")}
          </h3>
          <RichTextViewer value={instructions} />
          <Divider section />
        </div>
      )}

      {requirements && (
        <div>
          <IconHeader
            as="h3"
            icon="envelope open outline"
            header={t("challenge.details.include")}
            content={<RichTextViewer value={requirements} />}
            wrapped={theme.sizes.isMobile}
          />
          <Divider section />
        </div>
      )}

      {helpfulTips && (
        <div>
          <IconHeader
            as="h3"
            icon="info circle"
            header={t("challenge.details.tips")}
            content={<RichTextViewer value={helpfulTips} />}
            wrapped={theme.sizes.isMobile}
          />
          <Divider section />
        </div>
      )}

      {((links && links.length > 0) ||
        (attachments && attachments.length > 0)) && (
        <div>
          <IconHeader
            as="h3"
            icon="folder"
            header={t("challenge.links.title")}
            content={
              <div>
                {links?.map((link) => (
                  <p key={link.link} style={{ marginBottom: 10 }}>
                    <strong>{link.title}</strong>
                    <br />
                    <a
                      className="with-border"
                      href={link.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.link}
                    </a>
                  </p>
                ))}
                {attachments?.map((attachment) => (
                  <p key={attachment.title} style={{ marginBottom: 10 }}>
                    <strong>{attachment.title}</strong>
                    <br />
                    <DownloadLink
                      url={attachment.url ?? ""}
                      name={attachment.name ?? attachment.title ?? ""}
                    >
                      {attachment.name ?? attachment.title ?? ""}
                    </DownloadLink>
                  </p>
                ))}
              </div>
            }
          />
          <Divider hidden />
        </div>
      )}

      {ipManagementInfo?.hasSensitiveInfo &&
        util.organisationFeaturesEnabled(user, ["ipManagement"]) && (
          <Message info>
            <h3>ADDITIONAL INFORMATION</h3>
            {!ipManagementInfo.userHasSigned ?
              <>
                <p>
                  <Icon name="info" />{" "}
                  <strong>
                    This information will be available once you have agreed to
                    and signed the IP statement for this challenge.
                  </strong>
                </p>
                <OverviewActionsContainer
                  style={{ alignItems: "flex-start", marginBottom: 0 }}
                >
                  {redactedInfoWidths.map((width, i) => (
                    <RedactedInfo key={i} width={width} />
                  ))}
                </OverviewActionsContainer>
              </>
            : <>
                <p>
                  <strong>
                    <Icon name="info" /> You can read this information because
                    you have signed the IP statement for this challenge.
                  </strong>
                </p>
                <RichTextViewer
                  value={ipStatement?.statementSensitiveInfo ?? ""}
                />
              </>
            }
            <Button
              secondary
              onClick={() => setIpStatementAgreementOpen(true)}
              icon="file alternate"
              content="View IP statement"
              style={{ marginTop: 10 }}
            />
          </Message>
        )}

      {stage === "published" && !user && (
        <OverviewActionsContainer>
          <OverviewActions>
            {challenge.ideaVisibility === "users" && (
              <Button
                style={{
                  border: `1px solid ${theme.primaryColour}`,
                  color: theme.primaryColour,
                  backgroundColor: "#fff",
                  marginBottom: 5,
                }}
                icon="unhide"
                content={t("challenge.viewOtherIdeas")}
                as={Link}
                to={`/challenges/${challenge._id}/ideas`}
              />
            )}

            <NewIdeaTrigger>
              <Button
                primary
                icon="plus"
                content={
                  challenge.buttonText ?
                    challenge.buttonText
                  : t("challenge.ideas.createYourIdea")
                }
                onClick={canMakeIdea ? newIdea : () => redirectToLogin()}
              />
              <Popup
                trigger={
                  <div style={{ alignSelf: "center", marginTop: 3 }}>
                    <FakeLink withBorder>
                      <Icon name="question circle" />
                      {t("challenge.details.ideaHelpTitle")}
                    </FakeLink>
                  </div>
                }
                content={t("challenge.details.ideaHelpDescription")}
              />
            </NewIdeaTrigger>

            {myUnsubmittedIdeas?.length === 1 && (
              <Button
                icon="lightbulb"
                content={t("challenge.continueIdea")}
                primary
                as={Link}
                to={`/ideas/${myUnsubmittedIdeas[0]?._id}`}
              />
            )}

            {myUnsubmittedIdeas?.length > 1 && (
              <Dropdown
                icon={null}
                trigger={
                  <Button
                    primary
                    icon="lightbulb"
                    content={t("challenge.continueIdea")}
                  />
                }
              >
                <Dropdown.Menu>
                  {myUnsubmittedIdeas.map((i) => (
                    <Dropdown.Item
                      key={i._id}
                      as={Link}
                      to={`/ideas/${i._id}`}
                      content={i.name}
                    />
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </OverviewActions>
        </OverviewActionsContainer>
      )}

      {stage === "published" && user && (
        <OverviewActionsContainer>
          <OverviewActions>
            {(
              (challenge.ideaCount !== undefined &&
                challenge.ideaCount >= 1 &&
                challenge.ideaVisibility === "users") ||
              (challengeDetail &&
                util.canManageChallenge(user, challengeDetail)) ||
              (challengeDetail &&
                util.hasPermission(
                  user,
                  "challenge.viewIdeas",
                  challengeDetail._id,
                ))
            ) ?
              <Button
                inverted
                basic
                primary
                icon="unhide"
                content={t("challenge.viewOtherIdeas")}
                as={Link}
                to={`/challenges/${challenge._id}/ideas`}
                className="challenge-tour-step-3"
              />
            : null}

            {(
              canMakeIdea &&
              (!myUnsubmittedIdeas || myUnsubmittedIdeas?.length === 0)
            ) ?
              <Button
                primary
                icon="plus"
                content={
                  challenge.buttonText ?
                    challenge.buttonText
                  : t("challenge.ideas.createYourIdea")
                }
                onClick={canMakeIdea ? newIdea : () => redirectToLogin()}
                disabled={!ipStatementHandled}
                className="challenge-tour-step-3"
              />
            : null}

            {myUnsubmittedIdeas?.length === 1 && (
              <Button
                icon="lightbulb"
                content={t("challenge.continueIdea")}
                primary
                as={Link}
                to={`/ideas/${myUnsubmittedIdeas[0]?._id}`}
              />
            )}

            {myUnsubmittedIdeas?.length > 1 && (
              <Dropdown
                icon={null}
                trigger={
                  <Button
                    style={{ width: "100%" }}
                    primary
                    icon="lightbulb"
                    content={t("challenge.continueIdea")}
                  />
                }
              >
                <Dropdown.Menu>
                  {myUnsubmittedIdeas.map((i) => (
                    <Dropdown.Item
                      key={i._id}
                      as={Link}
                      to={`/ideas/${i._id}`}
                      content={i.name}
                    />
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </OverviewActions>
          {(
            (challengeDetail &&
              util.canManageChallenge(user, challengeDetail)) ||
            (challengeDetail &&
              util.hasPermission(
                user,
                "challenge.editSettings",
                challengeDetail._id,
              ))
          ) ?
            <Input
              style={{ marginTop: 10 }}
              label="Login link"
              value={util.getBaseUrl(user?.ownerOrganisation)}
              readOnly
            />
          : null}
        </OverviewActionsContainer>
      )}
    </div>
  );
};

export default Overview;
